import { useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { MetaWrapper } from '../../components/Meta'
import Page from './Page'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../../apolloClient'
import { Redirect, useHistory } from 'react-router-dom'
import { CART_URL, getOrderHistoryUrl, HOME_URL } from '../../urls'
import { useCartClearMutation } from '@graphql/mutations/__generated__/cart.generated'
import useGlobalLoader from '../../hooks/useGlobalLoader'
import { useTranslation } from 'react-i18next'
import {
  useCreateOrderNoteMutation,
  useDeleteOrderMutation,
  useEditOrderMutation,
} from '@graphql/mutations/__generated__/order.generated'
import { useOrderNotesQuery } from '@graphql/queries/__generated__/order.generated'
import { maybe } from '../../utils/misc'
import { syncCartCache } from '../../utils/cartCache'
import { OrderNoteFragment } from '@graphql/fragments/__generated__/OrderNote.generated'
import Dialog from '@mui/material/Dialog'
import InfoDialog from '@components/InfoDialog'

const View: React.FC = ({}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { setLoading } = useGlobalLoader()
  const { enqueueSnackbar } = useSnackbar()
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const [isShowDialog, setIsShowDialog] = useState(false)

  const meQuery = useMeQuery()

  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: CartTypeEnum.Wholesale, includeLinesByCategory: true },
  })
  const orderNotesQuery = useOrderNotesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { first: 20, orderId: cartQueryResult.data?.cart?.orderId?.toString() },
    skip: !cartQueryResult.data?.cart?.orderId,
  })

  // a full clear deletes the cart (the payload has no cart then), so the Cart query has to be refetched
  const [cartClear] = useCartClearMutation({
    awaitRefetchQueries: true,
    refetchQueries: ({ data }) => (data?.cartClear.cart ? ['CartLines'] : ['CartLines', 'Cart']),
    update: (cache, { data }) => syncCartCache(cache, data?.cartClear.cart),
  })

  const [editOrder] = useEditOrderMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['CartLines', 'Cart'],
  })
  const [createOrderNote] = useCreateOrderNoteMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['OrderNotes'],
  })

  const [deleteOrder] = useDeleteOrderMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['CartLines', 'Cart'],
  })

  const handleClearCart = async (categoryId?: string) => {
    setLoading(true)
    try {
      await cartClear({
        variables: { cartType: CartTypeEnum.Wholesale, category: categoryId, includeLinesByCategory: true },
      })

      // redirect after clear all lines from cart
      if (!categoryId) {
        history.push(HOME_URL)
      }
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }
  const onSaveAsDraft = async () => {
    setLoading(true)
    try {
      const orderId = cartQueryResult.data?.cart?.orderId
      await editOrder({ variables: { orderId, isDraft: true } })
      history.push(getOrderHistoryUrl('draft'))
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }
  const onCreateOrderNote = async (values: { orderId: number; content: string; replyToMe: boolean }) => {
    setLoading(true)
    try {
      await createOrderNote({
        variables: { input: { orderId: values.orderId, content: values.content, replyToMe: values.replyToMe } },
      })
      enqueueSnackbar(t('snackbarMessages.success.orderNoteCreated', 'Note has been created'), { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }
  const onDeleteOrder = async () => {
    setLoading(true)
    setIsShowDialog(false)
    try {
      await deleteOrder({ variables: { pk: cartQueryResult.data.cart.orderId } })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }
  const showDeleteOrderDialog = () => {
    setIsShowDialog(true)
  }

  if (!isWholesaleMode || (!meQuery.loading && !meQuery.data?.me)) {
    return <Redirect to={CART_URL} />
  }
  const orderNotes = maybe<OrderNoteFragment[]>(() => orderNotesQuery.data.orderNotes.edges.map(edge => edge.node), [])
  return (
    <MetaWrapper titleDetails={t('metaTitle.wholesaleShoppingBag', 'Wholesale Shopping Bag')}>
      <Page
        cartQueryResult={cartQueryResult}
        cartClear={handleClearCart}
        onSaveAsDraft={onSaveAsDraft}
        onCreateOrderNote={onCreateOrderNote}
        orderNotes={orderNotes}
        onDeleteOrder={showDeleteOrderDialog}
      />
      <Dialog open={isShowDialog} onClose={() => setIsShowDialog(false)}>
        <InfoDialog
          title={t('delete', 'Delete')}
          content={<p>{t('orderWillBeDeleted', 'The order will be deleted. Are you sure?')}</p>}
          onClose={() => setIsShowDialog(false)}
          onSubmit={onDeleteOrder}
          cancelButtonText={t('button.no', 'No')}
          submitButtonText={t('button.yes', 'Yes')}
        />
      </Dialog>
    </MetaWrapper>
  )
}

export default View
